import { useMemo, useRef, useState, type FormEvent } from "react";
import { Page } from "../components/AppShell";
import { Badge, Banner, Card, Empty, PanelState } from "../components/ui";
import { useAuth } from "../auth/AuthContext";
import { api, apiForm } from "../lib/api";
import { date, num, titleCase } from "../lib/format";
import { useApi } from "../lib/useApi";
import type {
  RoomTemplate,
  SurfaceRow,
  TemplateCategory,
} from "../lib/types";

// ═══════════════════════════════════════════════════════════════════════════
//  ROOM TEMPLATES
// ═══════════════════════════════════════════════════════════════════════════
// The one page in this console that PUBLISHES. Everything else reports on what
// customers did; what happens here is on every customer's "Paint a Sample
// Room" screen as soon as it saves.
//
// Which is why it is wordier than the analytics pages. The two things an admin
// cannot see from a thumbnail — whether a mask has a paintable wall, and what
// a delete does to the projects already built on a template — are the two
// things that go wrong, so both are stated where the button is rather than
// left in a runbook.
//
// Uploads are a plain inline form, not a modal, matching the invite form on
// the team page: the surface report comes back WITH the response and is the
// reason to wait for it, and a modal that has to stay open to show you a
// result is a worse version of a form that does.

interface CategoryResponse {
  categories: TemplateCategory[];
}

interface TemplateResponse {
  templates: RoomTemplate[];
}

interface UploadResult {
  template: RoomTemplate;
  surfaces: SurfaceRow[];
}

export function TemplatesPage() {
  const { admin } = useAuth();
  // The same OWNER/ADMIN line the API draws on these routes. An ANALYST gets
  // the full picture and no buttons — the console is mostly analytics, and
  // this page is worth seeing even when you cannot change it.
  const canEdit = admin?.role === "OWNER" || admin?.role === "ADMIN";

  const categories = useApi<CategoryResponse>("/api/admin/template-categories");
  const templates = useApi<TemplateResponse>("/api/admin/templates");

  const [filter, setFilter] = useState<string>("");
  const [message, setMessage] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const rows = categories.data?.categories ?? [];
  const all = templates.data?.templates ?? [];

  const shown = useMemo(
    () => (filter ? all.filter((t) => t.category.id === filter) : all),
    [all, filter],
  );

  const withoutMask = all.filter((t) => !t.maskUrl).length;

  function reloadAll() {
    categories.reload();
    templates.reload();
  }

  /** Every mutation on this page goes through here, so one failure path. */
  async function act(fn: () => Promise<unknown>, success?: string) {
    setMessage(null);
    setNote(null);
    try {
      await fn();
      reloadAll();
      if (success) setNote(success);
      return true;
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "That didn't work.");
      return false;
    }
  }

  return (
    <Page
      title="Room templates"
      subtitle="The sample rooms customers paint, and the categories they sit under"
    >
      {message && <Banner tone="critical">{message}</Banner>}
      {note && <Banner tone="neutral">{note}</Banner>}

      {!canEdit && (
        <Banner title="You can see this but not change it">
          Uploading, editing and deleting templates needs the owner or admin
          role. Yours is {titleCase(admin?.role)}.
        </Banner>
      )}

      {canEdit && (
        <UploadForm
          categories={rows}
          onDone={reloadAll}
          onError={setMessage}
        />
      )}

      <Card
        title="Categories"
        note="The room-type chips in the customer app, in this order. Hiding one takes it off that screen without touching its templates or the projects already filed under it — which is almost always what you want instead of deleting."
        aside={`${num(rows.length)} categories`}
      >
        <PanelState
          loading={categories.loading}
          error={categories.error}
          empty={rows.length === 0}
          emptyText="No categories yet. Add one below to start uploading rooms."
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Templates</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Added</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <CategoryRow
                    key={row.id}
                    row={row}
                    canEdit={canEdit}
                    act={act}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </PanelState>

        {canEdit && <AddCategory act={act} />}

        <p className="card-note" style={{ marginTop: 12, maxWidth: "72ch" }}>
          A category's <strong>key</strong> is set when it is created and never
          changes. It is what every project stores as its room type, so renaming
          it would orphan the history — the editable name above is what
          customers and these dashboards actually display.
        </p>
      </Card>

      <Card
        title="Templates"
        note="Each one is a photo plus, ideally, a hand-drawn mask. With a mask the editor opens instantly; without one the first customer to pick that room waits while a mask is generated for them."
        aside={
          withoutMask > 0
            ? `${num(all.length)} rooms · ${num(withoutMask)} without a mask`
            : `${num(all.length)} rooms`
        }
      >
        {rows.length > 0 && (
          <div className="segmented" role="group" aria-label="Category">
            <button
              type="button"
              aria-pressed={filter === ""}
              onClick={() => setFilter("")}
            >
              All
            </button>
            {rows.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={filter === c.id}
                onClick={() => setFilter(c.id)}
              >
                {c.label} ({c.templates})
              </button>
            ))}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <PanelState
            loading={templates.loading}
            error={templates.error}
            empty={shown.length === 0}
            emptyText={
              all.length === 0
                ? "No templates yet. Upload one above."
                : "Nothing in this category yet."
            }
          >
            <div className="template-grid">
              {shown.map((t) => (
                <TemplateTile
                  key={t.id}
                  template={t}
                  categories={rows}
                  canEdit={canEdit}
                  act={act}
                  onError={setMessage}
                />
              ))}
            </div>
          </PanelState>
        </div>
      </Card>
    </Page>
  );
}

type Act = (fn: () => Promise<unknown>, success?: string) => Promise<boolean>;

// ─── Categories ─────────────────────────────────────────────────────────────

function CategoryRow({
  row,
  canEdit,
  act,
}: {
  row: TemplateCategory;
  canEdit: boolean;
  act: Act;
}) {
  const [label, setLabel] = useState(row.label);
  const [sortOrder, setSortOrder] = useState(String(row.sortOrder));
  const [busy, setBusy] = useState(false);

  const dirty = label !== row.label || sortOrder !== String(row.sortOrder);

  async function save() {
    setBusy(true);
    await act(
      () =>
        api(`/api/admin/template-categories/${row.id}`, {
          method: "PATCH",
          body: { label, sortOrder: Number(sortOrder) },
        }),
      `Saved "${label}".`,
    );
    setBusy(false);
  }

  async function toggle() {
    setBusy(true);
    await act(
      () =>
        api(`/api/admin/template-categories/${row.id}`, {
          method: "PATCH",
          body: { active: !row.active },
        }),
      row.active
        ? `"${row.label}" is hidden from the app.`
        : `"${row.label}" is live again.`,
    );
    setBusy(false);
  }

  async function remove() {
    // Confirmed in the browser rather than with a typed reason: the API already
    // refuses a category that holds templates, so the worst this can do is
    // remove an empty row — and it says what that leaves behind.
    const ok = window.confirm(
      `Delete "${row.label}"?\n\nProjects customers already filed under ` +
        `${row.key} keep that room type — they just stop pointing at a live ` +
        `category. This cannot be undone.`,
    );
    if (!ok) return;

    setBusy(true);
    const done = await act(
      () =>
        api(`/api/admin/template-categories/${row.id}`, { method: "DELETE" }),
      `Deleted "${row.label}".`,
    );
    if (!done) setBusy(false);
  }

  return (
    <tr>
      <td>
        {canEdit ? (
          <input
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            aria-label={`Name for ${row.key}`}
            maxLength={40}
            style={{ minWidth: 150 }}
          />
        ) : (
          <span className="cell-strong">{row.label}</span>
        )}
      </td>
      <td className="mono cell-mute">{row.key}</td>
      <td>{num(row.templates)}</td>
      <td>
        {canEdit ? (
          <input
            className="input"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            aria-label={`Order for ${row.key}`}
            inputMode="numeric"
            style={{ width: 70 }}
          />
        ) : (
          row.sortOrder
        )}
      </td>
      <td>
        {row.active ? (
          <Badge tone="good">Live</Badge>
        ) : (
          <Badge tone="warning">Hidden</Badge>
        )}
      </td>
      <td className="cell-mute">{date(row.createdAt)}</td>
      {canEdit && (
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {dirty && (
            <button
              className="btn btn-sm btn-primary"
              disabled={busy}
              onClick={() => void save()}
            >
              Save
            </button>
          )}{" "}
          <button className="btn btn-sm" disabled={busy} onClick={() => void toggle()}>
            {row.active ? "Hide" : "Show"}
          </button>{" "}
          <button
            className="btn btn-sm btn-danger"
            disabled={busy || row.templates > 0}
            // Spelled out, because a disabled button with no explanation is
            // the single most common "the console is broken" report.
            title={
              row.templates > 0
                ? `Holds ${row.templates} template${row.templates === 1 ? "" : "s"} — delete or move them first.`
                : "Delete this category"
            }
            onClick={() => void remove()}
          >
            Delete
          </button>
        </td>
      )}
    </tr>
  );
}

function AddCategory({ act }: { act: Act }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  // Shown live, because the key is derived from the name and is the one field
  // that cannot be changed afterwards. Mirrors categoryKeyFrom on the API.
  const key =
    label
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || "—";

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    const ok = await act(
      () =>
        api("/api/admin/template-categories", {
          method: "POST",
          body: { label: label.trim() },
        }),
      `Added "${label.trim()}". It goes to the end of the chip row — change its order above.`,
    );
    if (ok) setLabel("");
    setBusy(false);
  }

  return (
    <form
      onSubmit={submit}
      style={{
        marginTop: 14,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "flex-end",
      }}
    >
      <div className="field" style={{ margin: 0, flex: "1 1 220px" }}>
        <label htmlFor="newCategory">Add a category</label>
        <input
          id="newCategory"
          className="input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Home Theatre"
          maxLength={40}
        />
        <p className="field-hint">
          Key: <span className="mono">{key}</span> — set once, permanent.
        </p>
      </div>
      <button className="btn btn-primary" disabled={busy || !label.trim()}>
        {busy ? "Adding…" : "Add category"}
      </button>
    </form>
  );
}

// ─── Upload ─────────────────────────────────────────────────────────────────

function UploadForm({
  categories,
  onDone,
  onError,
}: {
  categories: TemplateCategory[];
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [categoryId, setCategoryId] = useState("");
  const [name, setName] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [mask, setMask] = useState<File | null>(null);
  const [keepThin, setKeepThin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);

  const imageRef = useRef<HTMLInputElement>(null);
  const maskRef = useRef<HTMLInputElement>(null);

  const preview = useMemo(
    () => (image ? URL.createObjectURL(image) : null),
    [image],
  );

  const chosen = categories.find((c) => c.id === categoryId);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!categoryId || !name.trim() || !image) return;

    setBusy(true);
    setResult(null);

    const form = new FormData();
    form.append("categoryId", categoryId);
    form.append("name", name.trim());
    form.append("image", image);
    if (mask) form.append("mask", mask);
    if (keepThin) form.append("keepThin", "true");

    try {
      const res = await apiForm<UploadResult>("/api/admin/templates", form);
      setResult(res);
      setName("");
      setImage(null);
      setMask(null);
      setKeepThin(false);
      if (imageRef.current) imageRef.current.value = "";
      if (maskRef.current) maskRef.current.value = "";
      onDone();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Couldn't upload that room.");
    } finally {
      setBusy(false);
    }
  }

  if (categories.length === 0) {
    return (
      <Card title="Upload a room">
        <Empty>Add a category first — every template lives in one.</Empty>
      </Card>
    );
  }

  return (
    <Card
      title="Upload a room"
      note="The photo is resized and re-encoded here, exactly as a customer's upload would be. A mask is optional; when you supply one it has to come off the same canvas as the photo, because it is lined up by aspect ratio."
    >
      <form onSubmit={submit}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div className="field" style={{ margin: 0, flex: "1 1 180px" }}>
            <label htmlFor="tplCategory">Category</label>
            <select
              id="tplCategory"
              className="input"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
            >
              <option value="">Choose…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                  {c.active ? "" : " (hidden)"}
                </option>
              ))}
            </select>
            {chosen && !chosen.active && (
              <p className="field-hint">
                That category is hidden, so this room won't appear in the app
                until it is switched back on.
              </p>
            )}
          </div>

          <div className="field" style={{ margin: 0, flex: "1 1 220px" }}>
            <label htmlFor="tplName">Room name</label>
            <input
              id="tplName"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Corner Nook"
              maxLength={80}
              required
            />
            <p className="field-hint">
              Shown under the thumbnail, and the default project name a customer
              gets.
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
          <div className="field" style={{ margin: 0, flex: "1 1 240px" }}>
            <label htmlFor="tplImage">Photo</label>
            <input
              id="tplImage"
              ref={imageRef}
              className="input"
              type="file"
              accept="image/*,.heic,.heif"
              onChange={(e) => setImage(e.target.files?.[0] ?? null)}
              required
            />
            <p className="field-hint">JPEG, PNG, WebP or HEIC, up to 12 MB.</p>
          </div>

          <div className="field" style={{ margin: 0, flex: "1 1 240px" }}>
            <label htmlFor="tplMask">Mask (optional)</label>
            <input
              id="tplMask"
              ref={maskRef}
              className="input"
              type="file"
              accept="image/png,image/*"
              onChange={(e) => setMask(e.target.files?.[0] ?? null)}
            />
            <p className="field-hint">
              Flat palette colours, same aspect ratio as the photo.
            </p>
          </div>
        </div>

        {mask && (
          <label
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              marginTop: 10,
              maxWidth: "72ch",
            }}
          >
            <input
              type="checkbox"
              checked={keepThin}
              onChange={(e) => setKeepThin(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span className="card-note" style={{ margin: 0 }}>
              <strong style={{ color: "var(--text-2)" }}>
                Keep thin bands
              </strong>{" "}
              — by default, 1–2px filaments of trim and ceiling-boundary are
              cleaned out, because snapping an antialiased edge manufactures
              them along every boundary. Tick this for an exterior that really
              does have a narrow fascia band to paint.
            </span>
          </label>
        )}

        {preview && (
          <div className="template-preview">
            <img src={preview} alt="" />
            <div className="card-note">
              {image?.name}
              {mask && (
                <>
                  <br />
                  mask: {mask.name}
                </>
              )}
            </div>
          </div>
        )}

        <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
          <button
            className="btn btn-primary"
            disabled={busy || !categoryId || !name.trim() || !image}
          >
            {busy ? "Processing…" : "Upload room"}
          </button>
          {busy && (
            <span className="card-note" style={{ margin: 0 }}>
              Resizing, letterboxing and counting the mask's surfaces — a few
              seconds.
            </span>
          )}
        </div>
      </form>

      {result && (
        <div style={{ marginTop: 14 }}>
          <Banner
            tone={result.template.maskUrl ? "neutral" : "warning"}
            title={`"${result.template.name}" is live in ${result.template.category.label}`}
          >
            {result.template.maskUrl
              ? " Its mask is stored, so the editor will open it instantly."
              : " No mask was supplied, so the first customer to pick this room waits while one is generated for them."}
          </Banner>
          {result.surfaces.length > 0 && (
            <SurfaceReport surfaces={result.surfaces} />
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * What the mask turned out to contain.
 *
 * The `kept` column is the point. Anything under its role's minimum area is
 * discarded by the editor as antialiasing noise — usually correct, and
 * occasionally a wall you meant to include that was drawn in the wrong colour.
 * This is the only screen where that is visible.
 */
function SurfaceReport({ surfaces }: { surfaces: SurfaceRow[] }) {
  return (
    <div className="table-wrap" style={{ marginTop: 10 }}>
      <table>
        <thead>
          <tr>
            <th>Surface</th>
            <th>Mask colour</th>
            <th>Share of the photo</th>
            <th>In the editor</th>
          </tr>
        </thead>
        <tbody>
          {surfaces.map((s) => (
            <tr key={`${s.role}-${s.hex}`}>
              <td className="cell-strong">{titleCase(s.role)}</td>
              <td>
                <span className="swatch swatch-sm" style={{ background: s.hex }} />{" "}
                <span className="mono cell-mute">{s.hex}</span>
              </td>
              <td>{s.percent}%</td>
              <td>
                {s.kept ? (
                  <Badge tone="good">Paintable</Badge>
                ) : (
                  <Badge>Ignored — too small</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── One template ───────────────────────────────────────────────────────────

function TemplateTile({
  template,
  categories,
  canEdit,
  act,
  onError,
}: {
  template: RoomTemplate;
  categories: TemplateCategory[];
  canEdit: boolean;
  act: Act;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(template.name);
  const [categoryId, setCategoryId] = useState(template.category.id);
  const [sortOrder, setSortOrder] = useState(String(template.sortOrder));
  const [busy, setBusy] = useState(false);
  const [surfaces, setSurfaces] = useState<SurfaceRow[] | null>(null);

  const replaceRef = useRef<HTMLInputElement>(null);
  const [replacement, setReplacement] = useState<File | null>(null);
  const replaceMaskRef = useRef<HTMLInputElement>(null);
  const [replacementMask, setReplacementMask] = useState<File | null>(null);

  async function save() {
    setBusy(true);
    const ok = await act(
      () =>
        api(`/api/admin/templates/${template.id}`, {
          method: "PATCH",
          body: { name, categoryId, sortOrder: Number(sortOrder) },
        }),
      `Saved "${name}".`,
    );
    if (ok) setEditing(false);
    setBusy(false);
  }

  async function replaceAssets() {
    if (!replacement) return;
    setBusy(true);
    setSurfaces(null);

    const form = new FormData();
    form.append("image", replacement);
    if (replacementMask) form.append("mask", replacementMask);

    try {
      const res = await apiForm<UploadResult>(
        `/api/admin/templates/${template.id}/assets`,
        form,
      );
      setSurfaces(res.surfaces);
      setReplacement(null);
      setReplacementMask(null);
      if (replaceRef.current) replaceRef.current.value = "";
      if (replaceMaskRef.current) replaceMaskRef.current.value = "";
      await act(async () => {}, `Replaced the photo for "${template.name}".`);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Couldn't replace that photo.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    const ok = window.confirm(
      `Delete "${template.name}"?\n\nIts photo and mask are removed from ` +
        `storage, so any project a customer started from this room loses its ` +
        `image. This cannot be undone.`,
    );
    if (!ok) return;

    setBusy(true);
    const done = await act(
      () => api(`/api/admin/templates/${template.id}`, { method: "DELETE" }),
      `Deleted "${template.name}".`,
    );
    if (!done) setBusy(false);
  }

  return (
    <div className="template-tile">
      {/* The thumbnail, falling back to the full image for a template stored
          before thumbnails existed. Run `npm run db:thumbnails` on the API to
          clear those — nothing here breaks meanwhile, it just loads a 2048px
          photo into a 230px tile. */}
      <img
        src={template.thumbnailUrl ?? template.imageUrl}
        alt={template.name}
        loading="lazy"
      />

      <div className="template-body">
        <div className="template-head">
          <strong>{template.name}</strong>
          {template.maskUrl ? (
            <Badge tone="good">Mask</Badge>
          ) : (
            <Badge tone="warning">No mask</Badge>
          )}
        </div>
        <div className="card-note" style={{ margin: 0 }}>
          {template.category.label} · order {template.sortOrder} ·{" "}
          {date(template.createdAt)}
        </div>

        {canEdit && !editing && (
          <div className="template-actions">
            <button className="btn btn-sm" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button
              className="btn btn-sm btn-danger"
              disabled={busy}
              onClick={() => void remove()}
            >
              Delete
            </button>
          </div>
        )}

        {canEdit && editing && (
          <div className="template-edit">
            <div className="field" style={{ margin: 0 }}>
              <label htmlFor={`name-${template.id}`}>Name</label>
              <input
                id={`name-${template.id}`}
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
              />
            </div>

            <div className="field" style={{ margin: 0 }}>
              <label htmlFor={`cat-${template.id}`}>Category</label>
              <select
                id={`cat-${template.id}`}
                className="input"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field" style={{ margin: 0 }}>
              <label htmlFor={`order-${template.id}`}>Order</label>
              <input
                id={`order-${template.id}`}
                className="input"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                inputMode="numeric"
              />
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn btn-sm btn-primary"
                disabled={busy}
                onClick={() => void save()}
              >
                Save
              </button>
              <button
                className="btn btn-sm"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  setName(template.name);
                  setCategoryId(template.category.id);
                  setSortOrder(String(template.sortOrder));
                }}
              >
                Cancel
              </button>
            </div>

            <div className="field" style={{ margin: 0 }}>
              <label htmlFor={`photo-${template.id}`}>Replace the photo</label>
              <input
                id={`photo-${template.id}`}
                ref={replaceRef}
                className="input"
                type="file"
                accept="image/*,.heic,.heif"
                onChange={(e) => setReplacement(e.target.files?.[0] ?? null)}
              />
              <input
                ref={replaceMaskRef}
                className="input"
                type="file"
                accept="image/png,image/*"
                aria-label="Replacement mask"
                style={{ marginTop: 6 }}
                onChange={(e) =>
                  setReplacementMask(e.target.files?.[0] ?? null)
                }
              />
              <p className="field-hint">
                The photo is required even to change only the mask — a mask is
                lined up against the photo it was drawn over. Uploading a photo
                without a mask clears the stored one.
              </p>
              <button
                className="btn btn-sm"
                type="button"
                disabled={busy || !replacement}
                onClick={() => void replaceAssets()}
              >
                {busy ? "Processing…" : "Replace"}
              </button>
            </div>

            {surfaces && surfaces.length > 0 && (
              <SurfaceReport surfaces={surfaces} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
